import React from "react";
import { apiUrl } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";

const SERVER_IMAGE_LIMIT = 12 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
]);

// Android/Samsung gallery pickers may expose a temporary File handle that can
// preview but later fails when fetch tries to stream it. Cache a normal in-memory
// File for each picker immediately after selection.
const materializedFiles = new WeakMap();

function setComposerMessage(sheet, text, isError = false) {
  let message = sheet.querySelector(".ga-photo-message");
  if (!(message instanceof HTMLElement)) {
    message = document.createElement("div");
    message.className = "ga-photo-message";
    const actions = sheet.querySelector(".ga-photo-actions");
    if (actions?.parentElement) actions.parentElement.insertBefore(message, actions);
    else sheet.appendChild(message);
  }
  message.textContent = text;
  message.style.color = isError ? "#ffc0ca" : "#fff";
}

function inferImageType(file) {
  const declared = String(file?.type || "").toLowerCase();
  if (SUPPORTED_IMAGE_TYPES.has(declared)) {
    return declared === "image/jpg" ? "image/jpeg" : declared;
  }

  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".gif")) return "image/gif";
  return "image/jpeg";
}

function readWithFileReader(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer && result.byteLength > 0) resolve(result);
      else reject(new Error("That photo could not be read from this phone."));
    };
    reader.onerror = () => reject(reader.error || new Error("That photo could not be read from this phone."));
    reader.onabort = () => reject(new Error("Photo selection was cancelled."));
    reader.readAsArrayBuffer(file);
  });
}

async function materializeFile(file) {
  if (!(file instanceof Blob)) throw new Error("Choose a photo from your device.");

  let bytes;
  try {
    if (typeof file.arrayBuffer !== "function") throw new Error("arrayBuffer unavailable");
    bytes = await file.arrayBuffer();
    if (!(bytes instanceof ArrayBuffer) || bytes.byteLength === 0) {
      throw new Error("empty photo");
    }
  } catch {
    bytes = await readWithFileReader(file);
  }

  const type = inferImageType(file);
  const rawName = String(file?.name || `ga-photo-${Date.now()}`).replace(/[^a-z0-9._-]/gi, "-");
  const fallbackExtension =
    type === "image/png" ? ".png" : type === "image/webp" ? ".webp" : type === "image/gif" ? ".gif" : ".jpg";
  const name = /\.[a-z0-9]{2,5}$/i.test(rawName) ? rawName : `${rawName}${fallbackExtension}`;

  return new File([bytes], name, {
    type,
    lastModified: Number(file?.lastModified || Date.now()),
  });
}

async function canvasToJpeg(canvas) {
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("Photo processing failed."))),
      "image/jpeg",
      0.88
    );
  });
  return new File([blob], `ga-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
}

async function shrinkLargeImage(file) {
  if (file.size <= SERVER_IMAGE_LIMIT && SUPPORTED_IMAGE_TYPES.has(String(file.type || "").toLowerCase())) {
    return file;
  }

  if (typeof createImageBitmap !== "function") {
    throw new Error("That photo is too large. Choose one under 12 MB.");
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
    const maxSide = 2200;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Photo processing is unavailable on this device.");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, 0, 0, width, height);
    const prepared = await canvasToJpeg(canvas);
    if (prepared.size > SERVER_IMAGE_LIMIT) {
      throw new Error("That photo is still too large. Choose a smaller image.");
    }
    return prepared;
  } catch (error) {
    throw new Error(error?.message || "That photo could not be prepared.");
  } finally {
    try {
      bitmap?.close?.();
    } catch {}
  }
}

export default function PhotoPostUploadFix() {
  React.useEffect(() => {
    const onFileChangeCapture = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== "file") return;
      if (!input.closest(".ga-photo-composer-sheet")) return;

      const selected = input.files?.[0];
      if (!selected) {
        materializedFiles.delete(input);
        return;
      }

      const promise = materializeFile(selected);
      materializedFiles.set(input, promise);
      promise.catch((error) => {
        const sheet = input.closest(".ga-photo-composer-sheet");
        if (sheet instanceof HTMLElement) {
          setComposerMessage(sheet, error?.message || "That photo could not be read from this phone.", true);
        }
      });
    };

    const onClickCapture = async (event) => {
      const button = event.target?.closest?.("button");
      if (!(button instanceof HTMLButtonElement)) return;
      if (String(button.textContent || "").trim().toLowerCase() !== "post photo") return;

      const sheet = button.closest(".ga-photo-composer-sheet");
      if (!(sheet instanceof HTMLElement)) return;

      const input = sheet.querySelector('input[type="file"][accept="image/*"]');
      const file = input?.files?.[0];
      if (!(input instanceof HTMLInputElement) || !file) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      const token = getAuthToken();
      if (!token) {
        setComposerMessage(sheet, "Sign in again before posting.", true);
        return;
      }

      const caption = String(sheet.querySelector(".ga-photo-caption textarea")?.value || "").trim();
      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = "Posting…";
      setComposerMessage(sheet, "Reading photo from phone…");

      try {
        const stableFile = await (materializedFiles.get(input) || materializeFile(file));
        materializedFiles.set(input, Promise.resolve(stableFile));

        setComposerMessage(sheet, "Preparing photo…");
        const uploadFile = await shrinkLargeImage(stableFile);
        const formData = new FormData();
        formData.append("image", uploadFile, uploadFile.name || `ga-photo-${Date.now()}.jpg`);
        formData.append("caption", caption);

        setComposerMessage(sheet, "Posting photo…");
        const response = await fetch(apiUrl("/posts/photo"), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          cache: "no-store",
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.ok) {
          throw new Error(data?.message || data?.error || `Photo upload failed (${response.status}).`);
        }

        const post = data.post || {};
        window.dispatchEvent(new CustomEvent("ga:feed-post-created", { detail: post }));
        window.dispatchEvent(new CustomEvent("ga:photo-post-created", { detail: post }));

        const closeButton = sheet.querySelector('button[aria-label="Close post composer"]');
        closeButton?.click?.();

        if (window.location.pathname === "/feed") {
          const target = post?.id ? `/feed?post=${encodeURIComponent(post.id)}` : "/feed";
          window.location.assign(target);
        }
      } catch (error) {
        const raw = String(error?.message || "");
        const message = /failed to fetch/i.test(raw)
          ? "Could not reach the GAStreaming upload server. Reopen the app and try once more."
          : raw || "Could not post this photo.";
        setComposerMessage(sheet, message, true);
        button.disabled = false;
        button.textContent = originalLabel || "Post photo";
      }
    };

    document.addEventListener("change", onFileChangeCapture, true);
    document.addEventListener("click", onClickCapture, true);
    return () => {
      document.removeEventListener("change", onFileChangeCapture, true);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, []);

  return null;
}
